class CollectionTabs extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      buttons: '[button-tab-cta]',
      products: '[tab-product-block]',
      viewAll: '[button-view-all]',
      tabsContainer: '.col-tabs__tabs',
      tabsWrapper: '.col-tabs__tabs-wrapper',
      indicator: '[data-indicator]',
      navPrev: '[data-nav-prev]',
      navNext: '[data-nav-next]',
      gradientLeft: '[data-gradient-left]',
      gradientRight: '[data-gradient-right]',
    }
    this.init();
  }
  
  init() {
    this.buttons = this.querySelectorAll(this.selectors.buttons);
    this.products = this.querySelectorAll(this.selectors.products);
    this.viewAll = this.querySelector(this.selectors.viewAll);
    this.tabsContainer = this.querySelector(this.selectors.tabsContainer);
    this.tabsWrapper = this.querySelector(this.selectors.tabsWrapper);
    this.indicator = this.querySelector(this.selectors.indicator);
    this.track = this.querySelector('.col-tabs__track');
    this.prevBtn = this.querySelector(this.selectors.navPrev);
    this.nextBtn = this.querySelector(this.selectors.navNext);
    this.gradientLeft = this.querySelector(this.selectors.gradientLeft);
    this.gradientRight = this.querySelector(this.selectors.gradientRight);
    
    // Add click handlers to navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollTabs(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollTabs(1));
    }
    
    // Add click handlers to tabs
    this.buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.buttons.forEach(prevBtn => {
          if (prevBtn.classList.contains('active')) {
            prevBtn.classList.remove('active');
          }
        });
        if (!btn.classList.contains('active')) {
          btn.classList.add('active');
          this.toggleProducts(btn);
          this.moveIndicator(btn);
          this.scrollToTab(btn);
        }
      });
    });
    
    // Initialize indicator position
    const activeTab = this.querySelector('[button-tab-cta].active');
    if (activeTab) {
      setTimeout(() => this.moveIndicator(activeTab), 100);
    }
    
    // Update scroll state on scroll
    if (this.tabsContainer) {
      this.tabsContainer.addEventListener('scroll', () => this.updateScrollState());
      this.updateScrollState();
      this.checkCentering();
    }
    
    // Handle window resize
    window.addEventListener('resize', () => {
      this.updateScrollState();
      this.checkCentering();
      const activeTab = this.querySelector('[button-tab-cta].active');
      if (activeTab) {
        this.moveIndicator(activeTab);
      }
    });
  }
  
  moveIndicator(button) {
    if (!this.indicator || !button) return;
    
    const buttonRect = button.getBoundingClientRect();
    const containerRect = this.tabsContainer.getBoundingClientRect();
    const scrollLeft = this.tabsContainer.scrollLeft;
    
    const left = buttonRect.left - containerRect.left + scrollLeft;
    const width = buttonRect.width;
    
    this.indicator.style.width = `${width}px`;
    this.indicator.style.transform = `translateX(${left}px)`;
  }
  
  scrollToTab(button) {
    if (!this.tabsContainer || !button) return;
    
    const containerRect = this.tabsContainer.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const scrollLeft = this.tabsContainer.scrollLeft;
    
    const buttonCenter = buttonRect.left - containerRect.left + scrollLeft + buttonRect.width / 2;
    const containerCenter = containerRect.width / 2;
    
    const scrollTo = buttonCenter - containerCenter;
    
    this.tabsContainer.scrollTo({
      left: scrollTo,
      behavior: 'smooth'
    });
  }
  
  scrollTabs(direction) {
    if (!this.tabsContainer) return;
    
    const scrollAmount = this.tabsContainer.clientWidth * 0.7;
    this.tabsContainer.scrollBy({
      left: direction * scrollAmount,
      behavior: 'smooth'
    });
  }
  
  updateScrollState() {
    if (!this.tabsContainer) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = this.tabsContainer;
    const maxScroll = scrollWidth - clientWidth;
    
    // Update button states
    if (this.prevBtn) {
      this.prevBtn.disabled = scrollLeft <= 5;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = scrollLeft >= maxScroll - 5;
    }
    
    // Update gradient active classes
    if (this.gradientLeft) {
      if (scrollLeft > 5) {
        this.gradientLeft.classList.add('active');
      } else {
        this.gradientLeft.classList.remove('active');
      }
    }
    
    if (this.gradientRight) {
      if (scrollLeft < maxScroll - 5) {
        this.gradientRight.classList.add('active');
      } else {
        this.gradientRight.classList.remove('active');
      }
    }
  }
  
  toggleProducts(button) {
    this.products.forEach(ele => {
      if (ele.classList.contains('active')) {
        ele.classList.remove('active');
      }
    });
    const productsDiv = this.querySelector(`[tab-product-block][data-id="${button.dataset.id}"]`);
    if (productsDiv && !productsDiv.classList.contains('active')) {
      productsDiv.classList.add('active');
      if (this.viewAll) {
        this.viewAll.href = productsDiv.dataset.url;
      }
    }
  }
  
  checkCentering() {
    if (!this.tabsContainer) return;
    
    const { scrollWidth, clientWidth } = this.tabsContainer;
    const isScrollable = scrollWidth > clientWidth;
    
    // Add 'centered' class when tabs don't need scrolling (both mobile and desktop)
    if (!isScrollable) {
      this.tabsContainer.classList.add('centered');
      this.setTrackWidth();
    } else {
      this.tabsContainer.classList.remove('centered');
      this.resetTrackWidth();
    }
  }
  
  setTrackWidth() {
    if (!this.track || !this.buttons || this.buttons.length === 0) return;
    
    // Calculate total width of all tabs including gaps
    let totalWidth = 0;
    const gap = parseFloat(getComputedStyle(this.tabsContainer).gap) || 0;
    
    this.buttons.forEach((button, index) => {
      totalWidth += button.offsetWidth;
      if (index < this.buttons.length - 1) {
        totalWidth += gap;
      }
    });
    
    // Set track width and center it
    this.track.style.width = `${totalWidth}px`;
    this.track.style.left = '50%';
    this.track.style.right = 'auto';
    this.track.style.transform = 'translateX(-50%)';
  }
  
  resetTrackWidth() {
    if (!this.track) return;
    
    // Reset to full width
    this.track.style.width = '';
    this.track.style.left = '0';
    this.track.style.right = '0';
    this.track.style.transform = '';
  }
}
customElements.define('collection-tabs', CollectionTabs);